import { Injectable, OnDestroy } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  ParamMap,
  Params,
  Router,
} from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { AnonymousSubject } from 'rxjs/internal/Subject';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DependencyList, useSubscriptionLatest } from '../core/hooks';
import { replayLast } from '../rx/util';
import { Param, ReadParams, WriteParams } from './route';

export class AngularReadParams implements ReadParams {
  constructor(private readonly delegate: ParamMap) {}

  get(key: string) {
    return this.delegate.get(key);
  }

  getAll(key: string) {
    return this.delegate.getAll(key);
  }
}

export class AngularWriteParams implements WriteParams {
  constructor(private readonly delegate: Params) {}

  set(key: string, value: string | null) {
    this.delegate[key] = value;
  }

  setAll(key: string, values: string[]) {
    this.delegate[key] = values;
  }
}

@Injectable()
export class RouteService implements OnDestroy {
  private params: Params = {};
  private readonly change = new Subject<unknown>();

  private readonly navigate = this.change.pipe(
    exhaustMap(() => Promise.resolve()),
    tap(() => {
      this.router.navigate([], {
        queryParams: this.params,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.params = {};
    }),
  );

  private readonly destroy = new Subject();
  ngOnDestroy() {
    this.destroy.next(undefined);
  }

  constructor(
    /**
     * Unused param. Instead, we use `getCompleteRoute(router)` which provides
     * a complete picture of the current URL.
     */
    private readonly route: ActivatedRoute,
    readonly router: Router,
  ) {
    this.navigate.subscribe();
    this.completeRoute.subscribe();
  }

  readonly completeRoute = this.router.events.pipe(
    takeUntil(this.destroy),
    filter(event => event instanceof NavigationEnd),
    // Start with something so we don't block hooks that use this
    startWith(null),
    map(() => getCompleteRoute(this.router)),
    replayLast(),
  );

  getQuery<T>(param: Param<T>) {
    return this.completeRoute.pipe(
      map(route => route.queryParamMap),
      map(params => param.get(new AngularReadParams(params))!),
      distinctUntilChanged(param.equals),
    );
  }

  getPath<T>(param: Param<T>) {
    return this.completeRoute.pipe(
      map(route => route.paramMap),
      map(params => {
        return param.get(params);
      }),
      distinctUntilChanged(param.equals),
    );
  }

  getPathOrQuery<T>(param: Param<T>): Observable<T> {
    return this.getPath(param).pipe(
      mergeMap(pathParam =>
        pathParam
          ? of(pathParam)
          : this.getQuery(param).pipe(
              catchError(err1 => {
                throw new Error(`Error in getQuery: ${err1}`);
              }),
            ),
      ),
      catchError(err2 =>
        this.getQuery(param).pipe(
          catchError(err1 => {
            throw new Error(
              `Error in getQuery: ${err1}, Error in getPath: ${err2}`,
            );
          }),
        ),
      ),
    );
  }

  query<T>(param: Param<T>, setDebounce?: number): Subject<T> {
    return new AnonymousSubject(
      this.setQuery(param, setDebounce),
      this.getQuery(param),
    );
  }

  queryFor<T>(param: Observable<Param<T>>): Subject<T> {
    const subject = new Subject<T>();
    subject
      .pipe(switchMap(value => param.pipe(map(param => ({ param, value })))))
      .subscribe(({ param, value }) => {
        this.setQuery(param).next(value);
      });
    return new AnonymousSubject(
      subject,
      param.pipe(switchMap(param => this.getQuery(param))),
    );
  }

  setQuery<T>(param: Param<T>, debounce: number = 0): Subject<T> {
    const subject = new Subject<T>();
    subject.pipe(debounce ? debounceTime(debounce) : tap()).subscribe(value => {
      param.set(new AngularWriteParams(this.params), value);
      this.change.next(undefined);
    });
    return subject;
  }
}

export function getCompleteRoute(router: Router): ActivatedRouteSnapshot {
  let route = router.routerState.snapshot.root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route;
}

export function getSyncQueryParam<T>(
  routeService: RouteService,
  param: Param<T>,
) {
  return param.get(
    new AngularReadParams(getCompleteRoute(routeService.router).queryParamMap),
  );
}

export function useQueryParam<T>(
  routeService: RouteService,
  param: Param<T>,
  debounce?: number,
  deps?: DependencyList,
): [T, (value: T) => void] {
  const query = routeService.query(param, debounce);
  const value =
    useSubscriptionLatest(query, [deps]) ||
    getSyncQueryParam(routeService, param);
  const setValue = (value: T) => query.next(value);
  return [value, setValue];
}

export function usePathParam<T>(
  routeService: RouteService,
  param: Param<T>,
): T {
  return param.get(
    new AngularReadParams(getCompleteRoute(routeService.router).paramMap),
  );
}

export function useActivatedRouteDataParam(
  route: ActivatedRoute,
  propertyName: string,
  deps?: DependencyList,
) {
  const benefitTypeParam = useSubscriptionLatest(
    route.data.pipe(map(data => data[propertyName])),
    [deps],
  );
  return benefitTypeParam;
}
